<template>
  <v-bottom-navigation :height="48" :active="true">
    <v-container class="d-flex align-center justify-space-between w-100 h-100">
      <div class="d-flex align-center justify-start mobile-app-bar-flex-gap mobile-app-bar-home-with-content-tree">
        <NuxtLink to="/" class="mobile-app-bar-home-button" @click="resetContentMenuStore">
          <v-icon icon="fa-solid fa-house" />
        </NuxtLink>

        <div v-if="pdfPages" class="d-flex align-center mobile-app-bar-flex-gap mobile-app-bar-content-tree">
          <div class="d-flex align-center mobile-app-bar-flex-gap">
            <v-icon class="mobile-app-bar-caret-icon" icon="fa-solid fa-caret-right" />
            <p>{{ pdfPageName }}</p>
          </div>
          <div v-if="pdfPageGrade" class="d-flex align-center mobile-app-bar-flex-gap">
            <v-icon class="mobile-app-bar-caret-icon" icon="fa-solid fa-caret-right" />
            <p>{{ pdfPageGrade }}</p>
          </div>
          <div v-if="pdfPageSubject" class="d-flex align-center mobile-app-bar-flex-gap">
            <v-icon class="mobile-app-bar-caret-icon" icon="fa-solid fa-caret-right" />
            <p>{{ pdfPageSubject }}</p>
          </div>
        </div>

        <div v-else class="d-flex align-center mobile-app-bar-flex-gap mobile-app-bar-content-tree">
          <div v-if="activeSubject && activeTab !== 'subjects'" class="d-flex align-center mobile-app-bar-flex-gap">
            <v-icon class="mobile-app-bar-caret-icon" icon="fa-solid fa-caret-right" />
            <p>{{ activeSubject.title }}</p>
          </div>
          <div v-if="activeSection && activeTab === 'smart-files'" class="d-flex align-center mobile-app-bar-flex-gap">
            <v-icon class="mobile-app-bar-caret-icon" icon="fa-solid fa-caret-right" />
            <p>{{ activeSection.title }}</p>
          </div>
        </div>
      </div>
      <v-icon class="mobile-app-bar-content-menu-icon" :icon="sidebarIcon" @click="toggleContentMenu" />
    </v-container>
  </v-bottom-navigation>
</template>

<script lang="ts">
import find from "lodash/find"

import { contentMenuVisible, pdfContentMenuVisible } from "~/helpers"
import http from "~/services/http"
import { useContentMenuStore } from "~/stores/contentMenuStore"
import { useDrawerStore } from "~/stores/drawerStore"
import { usePdfStore } from "~/stores/pdfStore"
import { GradeType, SubjectType } from "~/types/schema/index"

interface ISubjectsAndGrades {
  success: boolean
  grades: GradeType[]
  subjects: SubjectType[]
}

export default {
  setup() {
    const drawerStore = useDrawerStore()
    const contentMenuStore = useContentMenuStore()
    const pdfStore = usePdfStore()

    useHead({ bodyAttrs: { "data-helpscout": "mobile" } })

    return { drawerStore, contentMenuStore, pdfStore }
  },

  data() {
    return {
      subjectsAndGrades: null as null | ISubjectsAndGrades,
    }
  },

  computed: {
    pageSlug() {
      return this.$route.path.split("/")[1]
    },

    pdfPages() {
      return ["e-gramatas", "darba-lapas", "metodiskie-lidzekli"].includes(this.pageSlug)
    },

    pdfPageName() {
      switch (this.pageSlug) {
        case "e-gramatas":
          return "E-grāmatas"
        case "darba-lapas":
          return "Darba lapas"
        case "metodiskie-lidzekli":
          return "Metodiskie līdzekļi"
      }
      return ""
    },

    pdfPageGrade() {
      if (this.pdfPages && this.subjectsAndGrades !== null && this.pdfStore.currentFile) {
        const gradeId = this.pdfStore.currentFile.product.grade ? this.pdfStore.currentFile.product.grade.id : -1
        const pageGrade = this.subjectsAndGrades.grades.find((grade) => grade.id === gradeId)
        return pageGrade ? pageGrade.title : ""
      }
      return ""
    },

    pdfPageSubject() {
      if (this.pdfPages && this.subjectsAndGrades !== null && this.pdfStore.currentFile) {
        const subjectId = this.pdfStore.currentFile.product.subject ? this.pdfStore.currentFile.product.subject.id : -1
        const pageSubject = this.subjectsAndGrades.subjects.find((subject) => subject.id === subjectId)
        return pageSubject ? pageSubject.title : ""
      }
      return ""
    },

    sidebarIcon() {
      return contentMenuVisible(this.$route) || pdfContentMenuVisible(this.$route)
        ? "fa-solid fa-list-ol"
        : "fa-regular fa-filter-list"
    },

    activeTab() {
      return this.contentMenuStore.tab
    },

    activeSubject() {
      return this.contentMenuStore.subject
    },

    activeGrade() {
      return this.contentMenuStore.grade
    },

    activeSection() {
      return this.contentMenuStore.section
    },

    activeChild() {
      if (this.activeSection) {
        return find(this.activeSection.children, (child) => {
          return child.slug === this.$route.params.smartFileSlug
        })
      } else {
        return null
      }
    },
  },

  watch: {
    $route: {
      handler() {
        if (this.pdfPages && this.subjectsAndGrades === null) {
          http.get({
            url: "v2/subjects-and-grades",
            successCallback: this.successCallback,
          })
        }
      },
      immediate: true,
    },
  },

  methods: {
    successCallback(ctx: any) {
      this.subjectsAndGrades = ctx.response._data
    },

    toggleContentMenu() {
      this.$vuetify.display.xs ? this.drawerStore.closeMobileDrawer() : this.drawerStore.closeDesktopDrawer()
      this.drawerStore.toggleContentMenu()
    },

    resetContentMenuStore() {
      this.contentMenuStore.reset()
    },
  },
}
</script>

<style lang="sass">
.mobile-app-bar-content-menu-icon
  width: 16px
  font-size: 16px

.mobile-app-bar-home-with-content-tree
  flex: 1
  max-width: calc(100% - 16px)

.mobile-app-bar-content-tree
  overflow-x: auto
  white-space: nowrap
  max-width: calc(100% - 30px)
  flex: 1

.mobile-app-bar-home-button
  width: 18px
  text-decoration: none
  color: black

.mobile-app-bar-flex-gap
  gap: 7px

.mobile-app-bar-caret-icon
  font-size: 7px
</style>
